if (!customElements.get('cs-wave1-sticky-cart')) {
  customElements.define('cs-wave1-sticky-cart', class extends HTMLElement {
    connectedCallback() {
      this.primaryButton = document.getElementById(this.dataset.primaryButton);
      if (!this.primaryButton) return;
      this.productInfo = this.closest('product-info');
      this.stickyForm = this.querySelector('.sticky-cart__form form');
      this.stickyButton = this.stickyForm?.querySelector('[type="submit"]');
      this.selectedOptions = this.querySelector('[data-cs-selected-options]');
      this.choicePrompted = false;
      this.guideToChoices = this.guideToChoices.bind(this);
      this.onPrimaryChoice = this.onPrimaryChoice.bind(this);
      this.onStickyClick = (event) => {
        if (this.needsChoicePrompt()) this.guideToChoices(event);
      };
      this.onStickySubmit = (event) => {
        if (this.needsChoicePrompt()) this.guideToChoices(event);
      };
      this.stickyButton?.addEventListener('click', this.onStickyClick, true);
      this.stickyForm?.addEventListener('submit', this.onStickySubmit, true);
      this.productInfo?.addEventListener('change', this.onPrimaryChoice, true);
      this.primaryButtonObserver = new MutationObserver(() => this.syncVariantState());
      this.primaryButtonObserver.observe(this.primaryButton, { attributes: true, attributeFilter: ['disabled'], childList: true, characterData: true, subtree: true });
      this.inert = true;
      this.update = this.update.bind(this);
      this.queueUpdate = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      window.addEventListener('scroll', this.queueUpdate, { passive: true });
      window.addEventListener('resize', this.queueUpdate, { passive: true });
      window.addEventListener('load', this.queueUpdate, { once: true });
      this.dock = document.querySelector('.mobile-dock');
      if (this.dock) this.dock.addEventListener('transitionend', this.queueUpdate);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.queueUpdate);
        this.resizeObserver.observe(this.productInfo || this.primaryButton);
        if (this.dock) this.resizeObserver.observe(this.dock);
      }
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.productInfo?.dataset.section) return;
        this.syncVariantState(data.variant);
        if (this.hasAttribute('data-cs-cu-variant-preview')) this.syncCuVariantState();
      });
      this.syncVariantState();
      if (this.hasAttribute('data-cs-cu-variant-preview')) this.syncCuVariantState();
      this.update();
      this.queueUpdate();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.queueUpdate);
      window.removeEventListener('resize', this.queueUpdate);
      window.removeEventListener('load', this.queueUpdate);
      if (this.dock) this.dock.removeEventListener('transitionend', this.queueUpdate);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      this.primaryButtonObserver?.disconnect();
      this.stickyButton?.removeEventListener('click', this.onStickyClick, true);
      this.stickyForm?.removeEventListener('submit', this.onStickySubmit, true);
      this.productInfo?.removeEventListener('change', this.onPrimaryChoice, true);
      document.documentElement.classList.remove('cs-wave1-sticky-visible');
    }
    needsChoicePrompt() {
      return window.matchMedia('(max-width: 749px)').matches && this.hasAttribute('data-cs-multiple-variants')
        && !this.choicePrompted && !!this.stickyButton && !this.stickyButton.disabled;
    }
    guideToChoices(event) {
      const picker = this.productInfo?.querySelector('variant-selects[data-primary]');
      if (!picker) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.choicePrompted = true;
      this.updateStickyButtonText();
      picker.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      const firstChoice = picker.querySelector('input[type="radio"]:checked:not([disabled]), select, input[type="radio"]:not([disabled])');
      if (firstChoice) requestAnimationFrame(() => firstChoice.focus({ preventScroll: true }));
    }
    onPrimaryChoice(event) {
      if (!event.target.closest('variant-selects[data-primary]')) return;
      this.choicePrompted = true;
      this.updateStickyButtonText();
    }
    updateStickyButtonText() {
      if (!this.stickyButton) return;
      const text = this.needsChoicePrompt() ? this.dataset.chooseText : this.primaryButton.querySelector('span')?.textContent?.trim() || this.primaryButton.textContent.trim();
      const label = this.stickyButton.querySelector('span') || this.stickyButton;
      if (label.textContent.trim() !== text) label.textContent = text;
    }
    syncVariantState(variant) {
      const primaryInput = this.primaryButton.closest('product-form')?.querySelector('input[name="id"]');
      const stickyInput = this.stickyForm?.querySelector('input[name="id"]');
      if (primaryInput && stickyInput) {
        stickyInput.value = primaryInput.value;
        stickyInput.disabled = primaryInput.disabled || !primaryInput.value;
      }
      if (this.stickyButton) this.stickyButton.disabled = this.primaryButton.disabled || !stickyInput?.value;
      if (variant && this.selectedOptions) {
        const indexes = (this.dataset.choiceIndexes || '').split(',').filter(Boolean).map(Number);
        this.selectedOptions.textContent = indexes.map(index => variant.options?.[index]).filter(Boolean).join(' / ');
      } else if (variant === null && this.selectedOptions) {
        this.selectedOptions.textContent = '';
      }
      this.updateStickyButtonText();
    }
    syncCuVariantState() {
      this.productInfo?.querySelectorAll('variant-selects[data-primary] input[data-crossout][disabled]')
        .forEach((input) => input.removeAttribute('disabled'));
    }
    update() {
      const rect = this.primaryButton.getBoundingClientRect();
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const root = document.documentElement;
      const dock = this.dock || document.querySelector('.mobile-dock');
      if (dock && !this.dock) {
        this.dock = dock;
        this.dock.addEventListener('transitionend', this.queueUpdate);
        if (this.resizeObserver) this.resizeObserver.observe(dock);
      }
      const dockHeight = isMobile && dock && getComputedStyle(dock).display !== 'none'
        ? Math.max(0, window.innerHeight - dock.getBoundingClientRect().top)
        : 0;
      root.style.setProperty('--cs-wave1-dock-h', Math.round(dockHeight) + 'px');
      const visibleBottom = window.innerHeight - dockHeight;
      const fullyVisible = rect.top >= 0 && rect.bottom <= visibleBottom;
      const show = isMobile ? !fullyVisible : rect.bottom < 0;
      this.updateStickyButtonText();
      this.toggleAttribute('animate', show);
      this.inert = !show;
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      root.classList.toggle('cs-wave1-sticky-visible', show);
    }
  });
}
